/**
 * Chat sidebar for natural language data logging.
 *
 * Features:
 * - Right-side drawer for chat-based input
 * - Real-time message display with user/system messages
 * - Undo capability for recently logged entries
 * - Manual form fallback button
 * - Invalidates queries on successful log for chart refresh
 */
import { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Stack,
  TextInput,
  ActionIcon,
  ScrollArea,
  Text,
  Divider,
  Button,
  Group,
  LoadingOverlay,
  Box,
  Paper,
  ThemeIcon,
  rem,
} from '@mantine/core';
import { IconSend, IconForms, IconCheck, IconX, IconPencil } from '@tabler/icons-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useQueryClient } from '@tanstack/react-query';
import {
  sendUnifiedChatLog,
  undoChatLog,
  undoDosingChatLog,
  confirmLivestockAdd,
  confirmLivestockDeceased,
  type DosingLoggedEntry,
  type LoggedEntry,
  type MatchingTestTask,
  type SpeciesCandidate,
  type LivestockMatch,
  type LivestockParsedData,
  type LivestockAddedEntry,
  type ConfirmLivestockAddRequest,
  type ConfirmLivestockDeceasedRequest,
} from '@/api/chat';
import { UserMessage, SuccessMessage, ErrorMessage } from './ChatMessage';
import { LivestockSelectionMessage } from './LivestockSelectionMessage';
import { DeceasedSelectionMessage } from './DeceasedSelectionMessage';
import { LivestockSuccessMessage } from './LivestockSuccessMessage';
import { EditDosingModal } from '@/components/EditDosingModal';
import { UnifiedManualEntryModal } from '@/components/UnifiedManualEntryModal';
import { AddLivestockModal } from '@/features/livestock';

interface Message {
  id: string;
  type: 'user' | 'success' | 'error' | 'livestock_selection' | 'deceased_selection' | 'livestock_success';
  content?: string;
  entry?: LoggedEntry;
  dosingEntry?: DosingLoggedEntry;
  livestockEntry?: LivestockAddedEntry;
  examples?: string[];
  canUndo?: boolean;
  undone?: boolean;
  matchingTask?: MatchingTestTask | null;
  // Livestock selection fields
  speciesCandidates?: SpeciesCandidate[];
  livestockMatches?: LivestockMatch[];
  livestockParsed?: LivestockParsedData;
  isDeceased?: boolean;
}

export interface ChatSidebarProps {
  opened: boolean;
  onClose: () => void;
  tankId: string;
  tankName: string;
  onOpenManualForm?: () => void;
}

export function ChatSidebar({
  opened,
  onClose,
  tankId,
  tankName,
  onOpenManualForm,
}: ChatSidebarProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [undoableIds, setUndoableIds] = useState<Set<number>>(new Set());
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingEntry, setEditingEntry] = useState<DosingLoggedEntry | null>(null);
  const [manualModalOpened, setManualModalOpened] = useState(false);
  const [livestockModalOpened, setLivestockModalOpened] = useState(false);
  const [livestockInitialSearch, setLivestockInitialSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // Focus input when opened
  useEffect(() => {
    if (opened) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  // Clear undo IDs when sidebar closes
  useEffect(() => {
    if (!opened) {
      setUndoableIds(new Set());
    }
  }, [opened]);

  const handleSubmit = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');

    // Add user message
    const userMsgId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, type: 'user', content: userMessage },
    ]);

    setLoading(true);
    try {
      const responseMsgId = crypto.randomUUID();
      const response = await sendUnifiedChatLog(tankId, userMessage);

      if (response.status === 'needs_selection') {
        // Handle livestock selection flow
        if (response.entry_type === 'livestock_add' && response.species_candidates) {
          setMessages((prev) => [
            ...prev,
            {
              id: responseMsgId,
              type: 'livestock_selection',
              content: response.message,
              speciesCandidates: response.species_candidates,
              livestockParsed: response.livestock_parsed,
            },
          ]);
        } else if (response.entry_type === 'livestock_deceased' && response.livestock_matches) {
          setMessages((prev) => [
            ...prev,
            {
              id: responseMsgId,
              type: 'deceased_selection',
              content: response.message,
              livestockMatches: response.livestock_matches,
              livestockParsed: response.livestock_parsed,
            },
          ]);
        }
      } else if (response.status === 'logged') {
        if (response.entry_type === 'dosing' && response.dosing_entry) {
          // Add success message with dosing entry
          setMessages((prev) => [
            ...prev,
            {
              id: responseMsgId,
              type: 'success',
              dosingEntry: response.dosing_entry,
              canUndo: true,
            },
          ]);

          // Track undoable entry
          setUndoableIds((prev) => new Set([...prev, response.dosing_entry!.id]));

          // Invalidate dosing queries to refresh charts
          queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
        } else if (response.entry_type === 'water_test' && response.water_test_entry) {
          // Add success message with water test entry
          setMessages((prev) => [
            ...prev,
            {
              id: responseMsgId,
              type: 'success',
              entry: response.water_test_entry,
              canUndo: true,
              matchingTask: response.matching_task ?? null,
            },
          ]);

          // Track undoable entry
          setUndoableIds((prev) => new Set([...prev, response.water_test_entry!.id]));

          // Invalidate water test queries to refresh charts
          queryClient.invalidateQueries({ queryKey: ['sensor-data', tankId] });
          queryClient.invalidateQueries({ queryKey: ['water-tests', tankId] });
          queryClient.invalidateQueries({ queryKey: ['latest-metrics', tankId] });
        } else if (
          (response.entry_type === 'livestock_add' || response.entry_type === 'livestock_deceased') &&
          response.livestock_entry
        ) {
          // Add success message with livestock entry
          setMessages((prev) => [
            ...prev,
            {
              id: responseMsgId,
              type: 'livestock_success',
              livestockEntry: response.livestock_entry,
              content: response.message,
              isDeceased: response.entry_type === 'livestock_deceased',
            },
          ]);

          // Invalidate livestock queries
          queryClient.invalidateQueries({ queryKey: ['livestock', tankId] });
          queryClient.invalidateQueries({ queryKey: ['livestock-summary', tankId] });
        }

        // Always invalidate dashboard on successful log
        queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      } else {
        // Add error message
        setMessages((prev) => [
          ...prev,
          {
            id: responseMsgId,
            type: 'error',
            content: response.message || 'Something went wrong',
            examples: response.examples,
          },
        ]);
      }
    } catch (error) {
      const errorMsgId = crypto.randomUUID();
      setMessages((prev) => [
        ...prev,
        {
          id: errorMsgId,
          type: 'error',
          content:
            error instanceof Error ? error.message : 'Something went wrong',
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleUndo = async (entryId: number, messageId: string, isDosing: boolean) => {
    try {
      if (isDosing) {
        await undoDosingChatLog(entryId);
        // Invalidate dosing queries
        queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      } else {
        await undoChatLog(entryId);
        // Invalidate water test queries
        queryClient.invalidateQueries({ queryKey: ['sensor-data', tankId] });
        queryClient.invalidateQueries({ queryKey: ['water-tests', tankId] });
      }

      // Always invalidate dashboard
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });

      // Remove from undoable set
      setUndoableIds((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });

      // Update message to show undone state
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, canUndo: false, undone: true } : msg
        )
      );
    } catch (error) {
      console.error('Undo failed:', error);
    }
  };

  const handleLivestockSelect = async (
    messageId: string,
    data: ConfirmLivestockAddRequest
  ) => {
    setLoading(true);
    try {
      const response = await confirmLivestockAdd(tankId, data);

      if (response.status === 'logged' && response.livestock_entry) {
        // Replace selection message with success message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  type: 'livestock_success' as const,
                  livestockEntry: response.livestock_entry,
                  content: response.message,
                  isDeceased: false,
                }
              : msg
          )
        );

        // Invalidate livestock queries
        queryClient.invalidateQueries({ queryKey: ['livestock', tankId] });
        queryClient.invalidateQueries({ queryKey: ['livestock-summary', tankId] });
        queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      } else {
        // Show error
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            type: 'error' as const,
            content: response.message || 'Failed to add livestock',
          },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: 'error' as const,
          content: error instanceof Error ? error.message : 'Failed to add livestock',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeceasedSelect = async (
    messageId: string,
    data: ConfirmLivestockDeceasedRequest
  ) => {
    setLoading(true);
    try {
      const response = await confirmLivestockDeceased(tankId, data);

      if (response.status === 'logged' && response.livestock_entry) {
        // Replace selection message with success message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  type: 'livestock_success' as const,
                  livestockEntry: response.livestock_entry,
                  content: response.message,
                  isDeceased: true,
                }
              : msg
          )
        );

        // Invalidate livestock queries
        queryClient.invalidateQueries({ queryKey: ['livestock', tankId] });
        queryClient.invalidateQueries({ queryKey: ['livestock-summary', tankId] });
        queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            type: 'error' as const,
            content: response.message || 'Failed to update livestock',
          },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          type: 'error' as const,
          content: error instanceof Error ? error.message : 'Failed to update livestock',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAlternatives = (searchQuery: string) => {
    setLivestockInitialSearch(searchQuery);
    setLivestockModalOpened(true);
  };

  const handleManualEntry = () => {
    if (onOpenManualForm) {
      onOpenManualForm();
    } else {
      setManualModalOpened(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title="Log Data"
        position="right"
        size={isMobile ? '100%' : 'sm'}
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 60px)',
          },
        }}
      >
        <Text size="xs" c="dimmed" mb="sm">
          Logging to: {tankName}
        </Text>

        <ScrollArea flex={1} viewportRef={scrollRef} mb="md">
          <Stack gap="sm">
            {messages.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                Type a measurement or dosing entry, like "nitrates 5", "alk 8.2", or "mag 60ml"
              </Text>
            )}
            {messages.map((msg) => {
              if (msg.type === 'user') {
                return <UserMessage key={msg.id} content={msg.content!} />;
              }
              if (msg.type === 'success' && msg.dosingEntry) {
                return (
                  <DosingSuccessMessage
                    key={msg.id}
                    entry={msg.dosingEntry}
                    canUndo={!!msg.canUndo && undoableIds.has(msg.dosingEntry.id)}
                    undone={!!msg.undone}
                    onUndo={() => handleUndo(msg.dosingEntry!.id, msg.id, true)}
                    onEdit={() => {
                      setEditingEntry(msg.dosingEntry!);
                      setEditModalOpened(true);
                    }}
                  />
                );
              }
              if (msg.type === 'success' && msg.entry) {
                return (
                  <SuccessMessage
                    key={msg.id}
                    entry={msg.entry}
                    canUndo={!!msg.canUndo && undoableIds.has(msg.entry.id)}
                    undone={!!msg.undone}
                    onUndo={() => handleUndo(msg.entry!.id, msg.id, false)}
                    matchingTask={msg.matchingTask}
                    tankId={tankId}
                  />
                );
              }
              if (msg.type === 'error') {
                return (
                  <ErrorMessage
                    key={msg.id}
                    message={msg.content!}
                    examples={msg.examples}
                    onManualEntry={handleManualEntry}
                  />
                );
              }
              if (msg.type === 'livestock_selection' && msg.speciesCandidates && msg.livestockParsed) {
                return (
                  <LivestockSelectionMessage
                    key={msg.id}
                    candidates={msg.speciesCandidates}
                    parsedData={msg.livestockParsed}
                    onSelect={(data) => handleLivestockSelect(msg.id, data)}
                    onViewAlternatives={handleViewAlternatives}
                    loading={loading}
                  />
                );
              }
              if (msg.type === 'deceased_selection' && msg.livestockMatches) {
                return (
                  <DeceasedSelectionMessage
                    key={msg.id}
                    matches={msg.livestockMatches}
                    onSelect={(data) => handleDeceasedSelect(msg.id, data)}
                    loading={loading}
                  />
                );
              }
              if (msg.type === 'livestock_success' && msg.livestockEntry) {
                return (
                  <LivestockSuccessMessage
                    key={msg.id}
                    entry={msg.livestockEntry}
                    message={msg.content || ''}
                    isDeceased={msg.isDeceased || false}
                  />
                );
              }
              return null;
            })}
          </Stack>
        </ScrollArea>

        <Divider mb="sm" />

        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconForms size={14} />}
          onClick={handleManualEntry}
          mb="sm"
        >
          Manual entry form
        </Button>

        <Box pos="relative">
          <LoadingOverlay visible={loading} loaderProps={{ size: 'sm' }} />
          <Group gap="xs">
            <TextInput
              ref={inputRef}
              placeholder="Log measurement or dosing (e.g., 'alk 8.2' or 'mag 60ml')"
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              flex={1}
              disabled={loading}
              styles={{
                input: {
                  fontSize: rem(16),
                },
              }}
            />
            <ActionIcon
              size="lg"
              variant="filled"
              onClick={handleSubmit}
              disabled={!input.trim() || loading}
            >
              <IconSend size={18} />
            </ActionIcon>
          </Group>
        </Box>
      </Drawer>

      {/* Edit Modal for dosing entries */}
      <EditDosingModal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          setEditingEntry(null);
        }}
        event={editingEntry ? {
          id: editingEntry.id,
          timestamp: editingEntry.recorded_at,
          pump_number: 0,
          source: 'manual',
          chemical_name: editingEntry.chemical_name,
          volume_ml: editingEntry.volume_ml,
          brand: editingEntry.brand,
        } : null}
      />

      {/* Unified Manual Entry Modal */}
      <UnifiedManualEntryModal
        opened={manualModalOpened}
        onClose={() => setManualModalOpened(false)}
      />

      {/* Add Livestock Modal (for viewing all alternatives) */}
      <AddLivestockModal
        opened={livestockModalOpened}
        onClose={() => {
          setLivestockModalOpened(false);
          setLivestockInitialSearch('');
        }}
        onSuccess={() => {
          setLivestockModalOpened(false);
          setLivestockInitialSearch('');
          queryClient.invalidateQueries({ queryKey: ['livestock', tankId] });
          queryClient.invalidateQueries({ queryKey: ['livestock-summary', tankId] });
          queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
        }}
        tankId={tankId}
        initialSearch={livestockInitialSearch}
      />
    </>
  );
}

/**
 * Format a datetime string as relative time.
 */
function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  return date.toLocaleDateString();
}

/**
 * Success message component for dosing entries.
 */
interface DosingSuccessMessageProps {
  entry: DosingLoggedEntry;
  onUndo: () => void;
  canUndo: boolean;
  undone?: boolean;
  onEdit: () => void;
}

function DosingSuccessMessage({
  entry,
  onUndo,
  canUndo,
  undone,
  onEdit,
}: DosingSuccessMessageProps) {
  const timeDisplay = formatTimeAgo(entry.recorded_at);

  return (
    <Paper p="sm" radius="md" withBorder style={undone ? { opacity: 0.6 } : undefined}>
      <Group gap="xs" mb={4}>
        <ThemeIcon size="sm" color={undone ? 'gray' : 'green'} variant="light" radius="xl">
          {undone ? <IconX size={14} /> : <IconCheck size={14} />}
        </ThemeIcon>
        <Text size="sm" fw={500} td={undone ? 'line-through' : undefined} c={undone ? 'dimmed' : undefined}>
          Logged {entry.chemical_name} {entry.volume_ml}ml
        </Text>
        {undone && (
          <Text size="xs" c="dimmed" fs="italic">
            (undone)
          </Text>
        )}
      </Group>
      {entry.brand && !undone && (
        <Text size="xs" c="dimmed" mb={4}>
          Brand: {entry.brand}
        </Text>
      )}
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed">
          {entry.tank_name} - {timeDisplay}
        </Text>
        {!undone && (
          <Group gap={4}>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconPencil size={12} />}
              onClick={onEdit}
            >
              Edit
            </Button>
            {canUndo && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconX size={12} />}
                onClick={onUndo}
              >
                Undo
              </Button>
            )}
          </Group>
        )}
      </Group>
    </Paper>
  );
}
